<template>
  <div class="content-calendar-new-page">
    <!--<StepsProgressBar class="content-calendar-new-page__progressbar" :currentStep="activeStep" :numberOfSteps="totalNumberOfSteps" :fontSize="19" color="teal" />-->

    <StepUploadContent
      class="content-calendar-new-page__step-container"
      :customerId="customerId"
      :galleryGroups="allPhotoGalleryGroups"
      :locations="savedLocations"
      :minimumAssetsForInitialPostGen="minimumAssetsForInitialPostGen"
      :processedCommunityUploadAssets="processedCommunityUploadAssets"
      @update:galleryGroups="updatedGalleryGroupsList => (allPhotoGalleryGroups = updatedGalleryGroupsList)"
      :isLoading="isLoadingPhotoGalleryGroups"
      @proceed="async () => await router.push({ name: 'CalendarPosts', params: { customerId } })"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useHead } from "@unhead/vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import { MetricSender } from "@/helpers/MetricSender";
import AdminContext from "@logic/AdminContext";

// Components
// steps
import StepUploadContent from "./steps/StepUploadContent.vue";

// Types
import { PhotoGalleryCustomerData } from "@contracts/photosPageData";
import type { LocationData } from "@contracts/socialLocations";

// Router
const route = useRoute();
const router = useRouter();
const remoteLogger = getGlobalRemoteLogger();

defineOptions({
  name: "CalendarUploadContent",
});

// Normalize route params ------------------------------------------------------
const customerId = computed<string>(() => (Array.isArray(route.params.customerId) ? route.params.customerId[0] : (route.params.customerId as string)));

const pageTitle = ref("Upload Photos");
useHead({ title: () => pageTitle.value });

// Load or generate photo galleries ===========================================
const allPhotoGalleryGroups = ref<Array<PhotoGalleryCustomerData>>([]);
const savedLocations = ref<Array<LocationData>>([]);
const minimumAssetsForInitialPostGen = ref<number>(0);
const processedCommunityUploadAssets = ref<number>(0);

const isLoadingPhotoGalleryGroups = ref<boolean>(true);

async function loadSavedLocations(): Promise<Array<LocationData>> {
  try {
    return await SocialLocationsRepo.list(customerId.value, false, false);
  } catch (error) {
    // Non-blocking: the existing upload groups still work without the location rows.
    remoteLogger?.error(`UploadContentPage.loadSavedLocations failed. customerId=${customerId.value} error=${error}`);
    return [];
  }
}

onMounted(async () => {
  const org = await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  if (org?.name) pageTitle.value = `Upload Photos - ${org.name}`;
  isLoadingPhotoGalleryGroups.value = true;
  // Load the upload groups (they get created on setup now)
  const [uploadGroups, uploadRequirements, locations] = await Promise.all([
    ContentCalendarRepo.loadUploadGroups(customerId.value),
    ContentCalendarRepo.loadUploadRequirements(customerId.value),
    loadSavedLocations(),
  ]);
  allPhotoGalleryGroups.value = uploadGroups;
  savedLocations.value = locations;
  minimumAssetsForInitialPostGen.value = uploadRequirements.minimumAssetsForInitialPostGen;
  processedCommunityUploadAssets.value = uploadRequirements.processedCommunityUploadAssets ?? 0;

  isLoadingPhotoGalleryGroups.value = false;

  MetricSender.sendMetric("Cust-Calendar-Uploads-PageView", { customerId: customerId.value });
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Content calendar new page ==================================================
.content-calendar-new-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__progressbar {
    margin-bottom: 40px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .content-calendar-new-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .content-calendar-new-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .content-calendar-new-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .content-calendar-new-page {
    width: $tablet-content-width;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .content-calendar-new-page {
    width: $mobile-content-width;
  }
}
</style>
